import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

export interface ProcessStageOption {
  value: string;
  label: string;
}

interface ProcessStageDropdownProps {
  options: ProcessStageOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
}

const PROCESS_STAGES: ProcessStageOption[] = [
  { value: 'open', label: 'Reported' },
  { value: 'false_alarm', label: 'False Alarm' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_site', label: 'On Site' },
  { value: 'in_analyzes', label: 'In Analyzes' },
  { value: 'decommissioning', label: 'Decommissioning' },
  { value: 'repair_begin', label: 'Repair Begin' },
  { value: 'waiting_parts', label: 'Waiting for Parts' },
  { value: 'repair_completed', label: 'Repair Completed' },
  { value: 'recommissioning', label: 'Recommissioning' },
  { value: 'recorded', label: 'Recorded' },
  { value: 'done', label: 'Done' },
];

export function ProcessStageDropdown({
  value,
  onChange,
  placeholder = 'Select stage...',
  disabled = false,
  loading = false,
}: ProcessStageDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = PROCESS_STAGES.find(opt => opt.value === value);
  const filteredOptions = PROCESS_STAGES.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Button/Trigger */}
      <div className="relative w-full">
        <button
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || loading}
          className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-left flex items-center justify-between hover:border-cyan-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-all"
        >
          <span className="flex-1">
            {loading ? (
              <span className="text-gray-400">Loading...</span>
            ) : selectedOption ? (
              <span className="text-gray-900 font-medium">{selectedOption.label}</span>
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </span>
          <ChevronDown
            size={18}
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Clear button - positioned absolutely */}
        {value && (
          <button
            onClick={handleClear}
            className="absolute right-12 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 z-10"
            title="Clear selection"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search stages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-100 transition-colors ${
                    value === option.value ? 'bg-cyan-50 border-l-4 border-cyan-500' : ''
                  }`}
                >
                  <span className={`text-sm font-medium ${value === option.value ? 'text-cyan-700' : 'text-gray-900'}`}>
                    {option.label}
                  </span>
                  {value === option.value && (
                    <Check size={18} className="text-cyan-600" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                No stages match your search
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
